import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Check,
  ArrowRight,
  Sun,
  ShieldCheck,
  Zap,
  Layers,
  Settings,
  Activity,
  Cpu
} from "lucide-react";

const SolarServicePage = () => {
  
  // 1. What's Included Steps Array
  const inclusionSteps = [
    { title: "Precision Site Survey", desc: "Drone orthomosaic layout rendering to analyze true shadow bounds and roof azimuth coordinates." },
    { title: "Granular Load Analysis", desc: "Logging 12-month DisCom billing cycles alongside connected load metrics to prevent over-sizing." },
    { title: "Bespoke System Design", desc: "3D structural modeling and PVsyst generation curve forecasting matching local radiation." },
    { title: "Solar Panel Installation", desc: "Deploying high-efficiency monocrystalline arrays anchored safely via torque-calibrated fasteners." },
    { title: "Inverter Synchronisation", desc: "Mounting IP65-rated smart string setups coupled cleanly with dedicated DC/AC distribution boxes." },
    { title: "System Wiring Infrastructure", desc: "Integrating multi-strand XLPE solar cables inside ultraviolet-protected conduit pipelines." },
    { title: "Net Metering Execution", desc: "End-to-end file drafting and liaison processing with state DisCom inspectors for bi-directional meters." },
    { title: "Testing & Commissioning", desc: "Executing string open-circuit voltage checks, earth resistance profiling, and final load sync." },
  ];

  // 2. Technical Specifications Sheet
  const techSpecs = [
    { parameter: "Plant Rated Capacity Scope", value: "3 kW up to 5 MW+ (Scalable Modular Architectures)" },
    { parameter: "Photovoltaic Module Technology", value: "A-Grade ALMM Listed Monocrystalline Half-Cut PERC / N-Type TopCon" },
    { parameter: "Inverter Technology Class", value: "Transformerless Multi-MPPT String Inverters with >98.6% Peak Efficiency" },
    { parameter: "Structural Mounting Base", value: "Hot-Dip Galvanized Iron (HDGI) 80 Microns / Anodized Aluminium Structurals" },
    { parameter: "Wind Load Tolerance Limits", value: "Certified up to 150 km/h matching structural stability codes" },
    { parameter: "Comprehensive Performance Shield", value: "10-Year Mechanical Framework Warranty | 25-Year Linear Power Output Protection" },
    { parameter: "System Safety Protocols", value: "Integrated Surge Protection Devices (SPD Class II), DC Fuse Blocks, Anti-Islanding" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-700 font-sans selection:bg-[#F7941D]/20 selection:text-[#0D3F7F]">
      
      {/* 🌟 1. HIGH-IMPACT DARK HERO SECTION 🌟 */}
      <section className="relative overflow-hidden pt-24 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-28 bg-[#050B14]">
        {/* Background Image with Premium Dark Gradient */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1920&q=90" 
            alt="Solar Panels"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/90 via-[#0D3F7F]/70 to-[#050B14]/95" />
        </div>
        
        {/* Ambient Glow */}
        <div className="absolute top-1/4 right-1/4 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/15 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none z-0" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-5 sm:space-y-6">
          <motion.div 
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-[#0D3F7F]/40 border border-[#1E5CB3]/40 px-3.5 py-1 sm:px-4 sm:py-1.5 rounded-full shadow-sm backdrop-blur-md"
          >
            <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#F7941D]" />
            <span className="text-[9px] sm:text-xs font-black tracking-widest text-white uppercase">
              ISO 9001:2015 Certified Infrastructure
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tight leading-[1.1] max-w-4xl mx-auto"
          >
            Industrial Grade <br />
            <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-md">
              Solar Architectures
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-300 text-xs sm:text-sm md:text-base max-w-2xl mx-auto font-medium leading-relaxed px-2 sm:px-0"
          >
            Turnkey grid integration, accelerated amortization rates, and tier-1 monocrystalline engineering optimized explicitly for long-term cash retention.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="pt-4 sm:pt-6 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 max-w-md sm:max-w-lg mx-auto w-full"
          >
            <Link to="/contact" className="group w-full sm:flex-1 bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-center py-3.5 sm:py-4 rounded-full font-bold text-xs sm:text-[14px] uppercase tracking-wider shadow-[0_8px_25px_rgba(247,148,29,0.3)] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
              <span>Get Free Quote</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/projectpage" className="group w-full sm:flex-1 bg-white/10 backdrop-blur-md border border-white/20 text-white font-bold py-3.5 sm:py-4 rounded-full text-center uppercase tracking-wider hover:bg-white/20 hover:border-white/40 transition-all duration-300 text-xs sm:text-[14px]">
              Explore Solutions
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 🌟 2. SERVICE OVERVIEW (Bespoke Framework) 🌟 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white border-b border-slate-200/60 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#0D3F7F]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center relative z-10">
          <div className="lg:col-span-5 space-y-4 sm:space-y-5 text-left">
            <div className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3 py-1 rounded-full">
              <Settings className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">Bespoke Framework</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#0F172A] tracking-tight leading-[1.15]">
              Complete Energy Autonomy & <br className="hidden sm:block" />
              <span className="text-[#F7941D]">Tariff Mitigation</span>
            </h2>
            
            <p className="text-slate-500 font-medium leading-relaxed text-xs sm:text-sm lg:text-[14px] border-l-[3px] border-[#0D3F7F] pl-4">
              Hamari engineering core aapke localized structural spatial bounds ko clean generation assets me badalti hai. Sourcing matrix strictly Tier-1 ALMM listed materials follow karti hai taaki har single array node performance curve index optimum respond kare.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { title: "Residential Sector", target: "Private Estates & Villas", benefit: "Protects your family budget from steep grid pricing hikes permanently. Lowers monthly utility bills by up to 85%." },
              { title: "Commercial Nodes", target: "Offices & Institutions", benefit: "Reduces peak-hour commercial tariff structures and directly enhances your enterprise's operational margins." },
              { title: "Industrial Complexes", target: "Factories & Warehouses", benefit: "Optimizes massive manufacturing loads cleanly, matches compliance, and leverages 40% corporate tax depreciation." },
            ].map((box, idx) => (
              <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 lg:p-6 flex flex-col justify-between shadow-sm hover:shadow-[0_10px_30px_rgba(13,63,127,0.06)] hover:border-[#F7941D]/40 transition-all duration-300 group">
                <div className="space-y-1.5 mb-3 sm:mb-4">
                  <h3 className="font-black text-[#0F172A] text-sm sm:text-[15px] tracking-tight group-hover:text-[#0D3F7F] transition-colors">{box.title}</h3>
                  <span className="text-[8px] sm:text-[9px] bg-[#F0F7FF] text-[#F7941D] px-2 py-0.5 rounded-md font-bold uppercase tracking-widest inline-block border border-[#1E5CB3]/10">
                    {box.target}
                  </span>
                </div>
                <p className="text-slate-500 text-[11px] sm:text-[12px] font-medium leading-relaxed border-t border-slate-100 pt-3 sm:pt-4">
                  {box.benefit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🌟 3. WHAT'S INCLUDED SECTION (Grid) 🌟 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F8FAFC] border-b border-slate-200/60 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3 py-1 rounded-full">
              <Layers className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">Full Lifecycle Deliverables</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0F172A] tracking-tight">Scope of Systems Engineering</h2>
          </div>

          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {inclusionSteps.map((step, idx) => (
              <div key={idx} className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-[0_10px_20px_rgba(247,148,29,0.06)] hover:border-[#F7941D]/30 transition-all duration-300 group">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0D3F7F]/5 text-[#0D3F7F] group-hover:bg-[#F7941D] group-hover:text-white flex items-center justify-center font-black text-xs sm:text-[14px] border border-[#0D3F7F]/10 mb-3 sm:mb-4 transition-colors duration-300">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="space-y-2">
                  <h3 className="font-bold text-[#0F172A] text-sm sm:text-[15px] tracking-tight leading-tight group-hover:text-[#F7941D] transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-slate-500 text-[11px] sm:text-[12px] font-medium leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 🌟 4. TECHNICAL SPECIFICATIONS SHEET (Dark Matrix Mode) 🌟 */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#050B14] relative overflow-hidden border-y border-[#1E5CB3]/20">
        
        {/* Cyber Grid Background */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '30px 30px' }} />
        <div className="absolute top-0 right-1/4 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] bg-[#1E5CB3]/20 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-[#0D3F7F]/40 border border-[#1E5CB3]/40 px-3 py-1 rounded-full mb-3 backdrop-blur-md">
              <Cpu className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold text-white uppercase tracking-widest">
                Engineering Standards
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tight">
              Technical Parameters Matrix
            </h2>
          </div>

          <div className="bg-[#08101E]/80 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-[1.5rem] overflow-hidden shadow-2xl">
            <div className="divide-y divide-white/5">
              {techSpecs.map((row, rIdx) => (
                <div key={rIdx} className="grid grid-cols-1 sm:grid-cols-12 p-4 sm:p-5 items-start sm:items-center gap-2 sm:gap-6 hover:bg-white/[0.02] transition-colors">
                  <div className="sm:col-span-5 text-[#F7941D] font-bold uppercase tracking-wider text-[10px] sm:text-[11px] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1E5CB3] shrink-0" />
                    {row.parameter}
                  </div>
                  <div className="sm:col-span-7 text-white font-medium text-xs sm:text-[14px] leading-snug pl-3.5 sm:pl-0 border-l sm:border-l-0 border-white/5">
                    {row.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 🌟 5. INSTALLATION TIMELINE PROCESS 🌟 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white border-b border-slate-200/60 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3 py-1 rounded-full">
              <Activity className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">Structured Deployment</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0F172A] tracking-tight">
              Execution Milestones Timeline
            </h2>
          </div>

          <div className="relative mt-6 sm:mt-8">
            {/* Horizontal Line for Desktop */}
            <div className="hidden lg:block absolute top-[28px] left-[8%] right-[8%] h-[2px] bg-slate-100 z-0">
               <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-[#0D3F7F] via-[#F7941D] to-[#0D3F7F] opacity-30" />
            </div>
            
            {/* Vertical Line for Mobile */}
            <div className="lg:hidden absolute left-[27px] top-6 bottom-6 w-[2px] bg-gradient-to-b from-[#0D3F7F] via-[#F7941D] to-[#0D3F7F] opacity-30 z-0" />

            <div className="grid grid-cols-1 lg:grid-cols-6 gap-5 sm:gap-6 lg:gap-4 relative z-10">
              {[
                { label: "Consultation", sub: "Load Audit & Analysis" },
                { label: "Site Survey", sub: "Drone Azimuth Profiling" },
                { label: "Proposal", sub: "PVsyst Yield Matrix" },
                { label: "Installation", sub: "Hardware Mounting Core" },
                { label: "Inspection", sub: "DisCom Net-Meter" },
                { label: "Handover", sub: "Live Grid Sync & App" },
              ].map((step, idx) => (
                <div key={idx} className="flex flex-row lg:flex-col items-center lg:items-center text-left lg:text-center group">
                  
                  {/* Glowing Node */}
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center text-sm sm:text-[15px] font-black text-[#0D3F7F] shadow-[0_4px_15px_rgba(0,0,0,0.05)] border-2 border-slate-100 relative group-hover:border-[#F7941D] group-hover:text-[#F7941D] group-hover:scale-110 transition-all duration-300 z-10 shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  
                  {/* Content Box */}
                  <div className="ml-4 sm:ml-5 lg:ml-0 lg:mt-5 bg-[#F8FAFC] border border-slate-200/80 rounded-xl p-3.5 sm:p-4 flex-1 w-full lg:w-[110%] xl:w-[120%] shadow-sm group-hover:bg-white group-hover:border-[#1E5CB3]/20 transition-colors">
                    <h4 className="font-extrabold text-[#0F172A] text-xs sm:text-[13px] md:text-[14px] tracking-tight group-hover:text-[#0D3F7F]">
                      {step.label}
                    </h4>
                    <p className="text-slate-500 font-medium text-[10px] sm:text-[11px] mt-1 leading-tight">
                      {step.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default SolarServicePage;